import React from 'react';
import { MemberSearchQuery_members } from '@frontend/app/queries/types/MemberSearchQuery';
import { TProject } from '@frontend/app/containers/Projects/types';
import { useMemberApplicantInfo } from '../../hooks/useMemberApplicantInfo';
import { adaptMemberApplicationFields } from '../../adapters/application-tab/member-application-fields-adapter';
import { adaptMemberBrief } from '../../adapters/application-tab/member-brief-adapter';
import { adaptMemberProduct } from '../../adapters/application-tab/member-product-adapter';
import { ApplicationHeader } from './ApplicationHeader';
import { FormFieldsSection } from './FormFieldsSection';
import { BriefSection } from './BriefSection';
import { ProductSection } from './ProductSection';
import { Skeleton } from '@/shadcn/components/ui/skeleton';

interface ApplicationTabProps {
  member: MemberSearchQuery_members | null;
  project: TProject;
  isLoading?: boolean;
}

export const ApplicationTab: React.FC<ApplicationTabProps> = ({
  member,
  project,
  isLoading = false,
}) => {
  const { memberApplicantInfo, isLoading: isLoadingApplicantInfo } = useMemberApplicantInfo({
    memberId: member?.id || null,
    programId: project?.id || null,
    skip: !member?.id || !project?.id,
  });

  const showLoading = isLoading || isLoadingApplicantInfo;

  // Transform data using adapters
  const applicationFieldsData = adaptMemberApplicationFields(member, project);
  const briefData = adaptMemberBrief(memberApplicantInfo);
  const productData = adaptMemberProduct(memberApplicantInfo);

  if (showLoading) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-6 w-32" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 overflow-hidden">
      <ApplicationHeader
        applicationTitle={project.title}
        applicationDate={memberApplicantInfo?.createdDate || member?.createdDate}
      />

      <div className="flex flex-col gap-3">
        <FormFieldsSection
          fields={applicationFieldsData.fields}
        />

        {briefData.showViewBrief && (
          <BriefSection
            deliverables={briefData.deliverables}
            compensation={briefData.compensation}
            onViewBrief={() => briefData.briefUrl && window.open(briefData.briefUrl, '_blank')}
            briefTemplate={memberApplicantInfo?.briefTemplate?.agreementIteration || null}
          />
        )}

        {productData.hasOrder && (
          <ProductSection
            productName={productData.productName}
            cost={productData.cost}
            catalogOrderRequest={memberApplicantInfo?.catalogOrderRequest || null}
          />
        )}
      </div>
    </div>
  );
};
